import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Lock,
  Unlock,
  Send,
  Radio,
  Wifi,
  WifiOff,
  MonitorPlay,
  Network,
  MessageSquare,
  ArrowLeft,
  PauseCircle,
  Trash2,
  ChevronDown,
} from 'lucide-react';
import { io, Socket as IOSocket } from 'socket.io-client';
import api from '../lib/api';
import {
  useRelaySession,
  useDeactivateRelaySession,
  useDeleteRelaySession,
} from '../hooks/useRelay';
import { RELAY_KIND_LABELS, type RelaySessionKind } from '../types/relay';
import {
  generateKey,
  encrypt,
  decrypt,
  toBase64,
  fromBase64,
  exportKey,
  importKey,
  hasSubtleCrypto,
} from '../lib/encryption';

interface Message {
  id: string;
  seq: number;
  content: Record<string, unknown>;
  localId?: string;
  createdAt: string;
  decrypted?: string;
}

function kindIcon(kind: RelaySessionKind | undefined) {
  switch (kind) {
    case 'instance':
      return MonitorPlay;
    case 'peer-runner':
      return Network;
    default:
      return MessageSquare;
  }
}

export default function RelaySessionPage() {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: session } = useRelaySession(sessionId);
  const deactivate = useDeactivateRelaySession();
  const deleteSession = useDeleteRelaySession();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sessionKey, setSessionKey] = useState<CryptoKey | null>(null);
  const [ready, setReady] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [teardownOpen, setTeardownOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastSeqRef = useRef<number>(0);
  const socketRef = useRef<IOSocket | null>(null);

  // Machine tunnels (instance / peer-runner) are read-only in the UI — no chat input.
  const isMachine = session?.kind === 'instance' || session?.kind === 'peer-runner';

  function startPolling() {
    pollRef.current = setInterval(() => pollNewMessages(), 2000);
  }

  function connectSocket() {
    if (!sessionId) return;

    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3100';
    const token = localStorage.getItem('token');

    const socket = io(`${baseUrl}/ws/relay`, {
      path: '/socket.io',
      auth: { apiKey: token },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      socket.emit('join_session', { sessionId });
    });

    socket.on('auth_success', () => {
      setSocketConnected(true);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    });

    socket.on('relay_message', async (data: { sessionId: string; message: Message }) => {
      const msg = await decryptMessage(data.message, sessionKey);
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        if (msg.localId && prev.some((m) => m.localId === msg.localId)) {
          return prev.map((m) => (m.localId === msg.localId ? msg : m));
        }
        return [...prev, msg];
      });
      if (msg.seq > lastSeqRef.current) {
        lastSeqRef.current = msg.seq;
      }
    });

    socket.on('message_ack', (data: { localId: string; id: string; seq: number }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.localId === data.localId ? { ...m, id: data.id, seq: data.seq } : m,
        ),
      );
      if (data.seq > lastSeqRef.current) {
        lastSeqRef.current = data.seq;
      }
    });

    socket.on('disconnect', () => {
      setSocketConnected(false);
      if (!pollRef.current) {
        startPolling();
      }
    });

    socketRef.current = socket;
  }

  async function initializeEncryption() {
    if (!hasSubtleCrypto) {
      setSessionKey(null);
      setReady(true);
      return;
    }

    try {
      const storedKey = sessionStorage.getItem(`relay-key-${sessionId}`);
      if (storedKey) {
        const key = await importKey(fromBase64(storedKey));
        setSessionKey(key);
      } else {
        const key = await generateKey();
        const exported = await exportKey(key);
        sessionStorage.setItem(`relay-key-${sessionId}`, toBase64(exported));
        setSessionKey(key);
      }
    } catch (err) {
      console.error('Encryption init failed, using plaintext:', err);
      setSessionKey(null);
    }
    setReady(true);
  }

  async function decryptMessage(msg: Message, key: CryptoKey | null): Promise<Message> {
    if (msg.content?.encrypted && key && hasSubtleCrypto) {
      try {
        const ciphertext = fromBase64(msg.content.encrypted as string);
        const plaintext = await decrypt(ciphertext, key);
        return { ...msg, decrypted: plaintext };
      } catch {
        return { ...msg, decrypted: '[decryption failed]' };
      }
    }
    if (msg.content?.text) {
      return { ...msg, decrypted: msg.content.text as string };
    }
    return msg;
  }

  async function loadMessages() {
    try {
      const res = await api.get(`/api/relay/sessions/${sessionId}/messages`);
      const raw: Message[] = res.data.messages || [];
      const decrypted = await Promise.all(raw.map((m) => decryptMessage(m, sessionKey)));
      setMessages(decrypted);
      if (raw.length > 0) {
        lastSeqRef.current = raw[raw.length - 1].seq;
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  }

  async function pollNewMessages() {
    try {
      const res = await api.get(
        `/api/relay/sessions/${sessionId}/messages?after_seq=${lastSeqRef.current}&limit=100`,
      );
      const raw: Message[] = res.data.messages || [];
      if (raw.length === 0) return;
      const decrypted = await Promise.all(raw.map((m) => decryptMessage(m, sessionKey)));
      setMessages((prev) => {
        const ids = new Set(raw.map((m) => m.id));
        const filtered = prev.filter((m) => !ids.has(m.id) && !ids.has(m.localId ?? ''));
        return [...filtered, ...decrypted];
      });
      lastSeqRef.current = raw[raw.length - 1].seq;
    } catch {
      // Polling failure is not critical
    }
  }

  useEffect(() => {
    if (!sessionId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    initializeEncryption();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!ready || !sessionId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadMessages();
    startPolling();
    connectSocket();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [ready, sessionId]);

  async function sendMessage() {
    if (!input.trim() || !sessionId || isMachine) return;

    const localId =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    let content: Record<string, unknown>;

    if (sessionKey && hasSubtleCrypto) {
      const encrypted = await encrypt(input, sessionKey);
      content = { type: 'chat', encrypted: toBase64(encrypted) };
    } else {
      content = { type: 'chat', text: input };
    }

    setMessages((prev) => [
      ...prev,
      {
        id: localId,
        seq: -1,
        content,
        localId,
        createdAt: new Date().toISOString(),
        decrypted: input,
      },
    ]);

    const text = input;
    setInput('');

    if (socketConnected && socketRef.current) {
      socketRef.current.emit('relay_message', { sessionId, content, localId });
    } else {
      try {
        await api.post(`/api/relay/sessions/${sessionId}/messages`, { content, localId });
      } catch (err) {
        console.error('Failed to send message:', err);
        setMessages((prev) =>
          prev.map((m) =>
            m.localId === localId ? { ...m, decrypted: `[send failed] ${text}` } : m,
          ),
        );
      }
    }
  }

  function handleDeactivate() {
    if (!sessionId) return;
    setTeardownOpen(false);
    deactivate.mutate(sessionId);
  }

  function handleDelete() {
    if (!sessionId) return;
    if (
      !window.confirm(
        'Delete this relay session? This permanently removes all stored messages and cannot be undone.',
      )
    ) {
      return;
    }
    deleteSession.mutate(sessionId, {
      onSuccess: () => navigate('/relay'),
    });
  }

  const Icon = kindIcon(session?.kind);
  const localEncrypted = hasSubtleCrypto && !!sessionKey;

  return (
    <div className="flex h-full flex-col bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4">
        <Link
          to="/relay"
          className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All sessions
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
              <Icon className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-slate-900">
                  {session?.tag || 'Relay Session'}
                </h1>
                {session && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                    {RELAY_KIND_LABELS[session.kind]}
                  </span>
                )}
                {socketConnected ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                    <Wifi className="h-3 w-3" />
                    Live
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                    <WifiOff className="h-3 w-3" />
                    Polling
                  </span>
                )}
              </div>
              <code className="text-xs text-slate-400">{sessionId?.slice(0, 12)}</code>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {session?.encrypted ? (
              <span
                className="inline-flex items-center gap-1 text-xs text-emerald-600"
                title="Server holds a data encryption key; stored content is ciphertext at rest."
              >
                <Lock className="h-4 w-4" />
                Encrypted at rest
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 text-xs text-amber-600"
                title="No server-side data encryption key was established for this session."
              >
                <Unlock className="h-4 w-4" />
                No server key
              </span>
            )}

            {/* Teardown menu */}
            <div className="relative">
              <button
                onClick={() => setTeardownOpen((o) => !o)}
                className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Teardown
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {teardownOpen && (
                <div className="absolute right-0 z-10 mt-1 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                  <button
                    onClick={handleDeactivate}
                    disabled={!session?.active || deactivate.isPending}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    <PauseCircle className="h-4 w-4 text-amber-500" />
                    <span>
                      <span className="font-medium">Deactivate</span>
                      <span className="block text-[11px] text-slate-400">
                        Graceful — keeps history
                      </span>
                    </span>
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleteSession.isPending}
                    className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50 disabled:opacity-40"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>
                      <span className="font-medium">Delete</span>
                      <span className="block text-[11px] text-red-400">
                        Destructive — drops all messages
                      </span>
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Endpoint / activity meta */}
        {session && (
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-500">
            <span>
              Live connections:{' '}
              <span className="font-medium text-slate-700">{session.liveConnections}</span>
            </span>
            <span>
              Events: <span className="font-medium text-slate-700">{session.seq}</span>
            </span>
            <span>
              State:{' '}
              <span className="font-medium text-slate-700">
                {session.active ? 'active' : 'inactive'}
              </span>
            </span>
            <span>Last active: {new Date(session.lastActiveAt).toLocaleString()}</span>
          </div>
        )}
      </div>

      {isMachine && (
        <div className="border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-700">
          This is a machine tunnel ({RELAY_KIND_LABELS[session!.kind]}). The activity stream below
          is read-only — the dashboard cannot inject messages into a machine protocol channel.
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {messages.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400">
            {isMachine ? 'No activity recorded yet.' : 'No messages yet. Send one below.'}
          </div>
        ) : isMachine ? (
          // Read-only activity stream for machine tunnels
          <div className="space-y-2">
            {messages.map((msg) => {
              const type = (msg.content?.type as string) || 'event';
              return (
                <div
                  key={msg.id || msg.localId}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600">
                        {type}
                      </span>
                      <span className="text-xs text-slate-400">
                        seq {msg.seq >= 0 ? msg.seq : 'pending'}
                      </span>
                    </span>
                    <span className="text-xs text-slate-400">
                      {new Date(msg.createdAt).toLocaleTimeString()}
                    </span>
                  </div>
                  <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-xs text-slate-600">
                    {msg.decrypted ?? JSON.stringify(msg.content, null, 2)}
                  </pre>
                </div>
              );
            })}
          </div>
        ) : (
          // Chat for manual sessions
          <div className="space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id || msg.localId}
                className="rounded-lg border border-slate-200 bg-white p-4"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs text-slate-400">
                    seq: {msg.seq >= 0 ? msg.seq : 'pending'}
                  </span>
                  <span className="text-xs text-slate-400">
                    {new Date(msg.createdAt).toLocaleTimeString()}
                  </span>
                </div>
                <p className="text-sm text-slate-700">
                  {msg.decrypted || JSON.stringify(msg.content)}
                </p>
              </div>
            ))}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input — manual sessions only */}
      {!isMachine && (
        <div className="border-t border-slate-200 bg-white p-4">
          {!localEncrypted && (
            <p className="mb-2 text-xs text-amber-600">
              Messages are sent in plaintext — a browser-local key could not be established. This
              key is not exchanged with any peer.
            </p>
          )}
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type a message..."
              className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim()}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Radio marker to keep the connection-status affordance visible on empty states */}
      {!session && (
        <div className="border-t border-slate-200 bg-white px-6 py-2 text-xs text-slate-400">
          <Radio className="mr-1 inline h-3 w-3" />
          Loading session…
        </div>
      )}
    </div>
  );
}
